/**
 * What a run's bar shows (AMO-306): a segment per step in run order, how
 * many steps are done, and the one thing to do next.
 *
 * @module components/workflows/RunBar.logic
 */
import type { WorkflowNodeType, WorkflowRun, WorkflowStepStatus } from "@amoreg/contracts";
import { workflowApproverEmails, workflowChecklistPeople, workflowTopologicalOrder } from "@repo/shared/workflowGraph";

export interface RunSegment {
  readonly nodeId: string;
  readonly label: string;
  /** The step's kind, so a waiting step says what it waits for (an approval, a permission, a person to do it). */
  readonly type: WorkflowNodeType;
  readonly status: WorkflowStepStatus;
}

/** A segment per step but Start, in run order, with its status (a step not reached yet is pending). */
export function runSegments(run: Pick<WorkflowRun, "graph" | "steps">): ReadonlyArray<RunSegment> {
  const byId = new Map(run.graph.nodes.map((node) => [node.id, node]));
  const status = new Map(run.steps.map((step) => [step.nodeId, step.status]));
  return workflowTopologicalOrder(run.graph).flatMap((id) => {
    const node = byId.get(id);
    if (!node || node.type === "start") return [];
    return [{ nodeId: id, label: node.label, type: node.type, status: status.get(id) ?? "pending" }];
  });
}

/** "3 of 7 steps": finished ones (done or skipped) of all but Start. */
export function runProgressLabel(segments: ReadonlyArray<RunSegment>): string {
  const done = segments.filter((segment) => segment.status === "succeeded" || segment.status === "skipped").length;
  return `${done} of ${segments.length} ${segments.length === 1 ? "step" : "steps"}`;
}

/** The one thing a run's bar offers now. */
export type RunAction =
  | { readonly kind: "stop" }
  /** A person must act at a step: an approval or a manual step. `mine` when it is the viewer. */
  | { readonly kind: "waiting"; readonly nodeId: string; readonly label: string; readonly people: ReadonlyArray<string>; readonly mine: boolean; readonly checklist: boolean }
  | { readonly kind: "retry"; readonly nodeId: string; readonly label: string }
  | { readonly kind: "again" }
  | { readonly kind: "none" };

const lower = (email: string | null | undefined) => email?.trim().toLowerCase() ?? null;

/**
 * What to do next: while it runs, Stop (or, when a step waits on a person,
 * who it waits on); failed, retry the first failed step if the viewer may;
 * finished or stopped, run it again.
 */
export function runMainAction(
  run: Pick<WorkflowRun, "status" | "graph" | "steps" | "startedByEmail">,
  viewer: { readonly email: string | null | undefined; readonly mayRetry: boolean },
): RunAction {
  const byId = new Map(run.graph.nodes.map((node) => [node.id, node]));
  const order = workflowTopologicalOrder(run.graph);
  const statusOf = new Map(run.steps.map((step) => [step.nodeId, step.status]));
  const viewerEmail = lower(viewer.email);
  if (run.status === "queued" || run.status === "running" || run.status === "waiting") {
    for (const id of order) {
      const node = byId.get(id);
      if (statusOf.get(id) !== "waiting" || !node) continue;
      if (node.type === "approval") {
        const people = workflowApproverEmails(node).filter((email) => email !== lower(run.startedByEmail));
        return { kind: "waiting", nodeId: id, label: node.label, people, mine: viewerEmail !== null && people.includes(viewerEmail), checklist: false };
      }
      if (node.type === "checklist") {
        const people = workflowChecklistPeople(node, lower(run.startedByEmail));
        return { kind: "waiting", nodeId: id, label: node.label, people, mine: viewerEmail !== null && people.includes(viewerEmail), checklist: true };
      }
    }
    return { kind: "stop" };
  }
  if (run.status === "failed") {
    const failed = order.find((id) => statusOf.get(id) === "failed");
    return failed && viewer.mayRetry ? { kind: "retry", nodeId: failed, label: byId.get(failed)?.label ?? failed } : { kind: "none" };
  }
  return { kind: "again" };
}

/** People as the bar names them: by name where known, else their address; "a, b and 2 more". */
export function peopleLabel(people: ReadonlyArray<string>, nameOf: (email: string) => string | null): string {
  const names = people.map((email) => nameOf(email) ?? email);
  if (names.length === 0) return "someone";
  if (names.length <= 2) return names.join(" or ");
  return `${names.slice(0, 2).join(", ")} or ${names.length - 2} more`;
}
